import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { skipToken } from "@reduxjs/toolkit/query"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { useBenefitScopeArg } from "@/features/benefit-workspace/useBenefitChildReads"
import { formatPersonName } from "@/lib/person-identity"

/** Current CMS identity comes only from the anchored file, never from frozen matching decisions. */
export function SourceCurrentApplication({ benefitId }: { benefitId: string }) {
  const scope = useBenefitScopeArg()
  const read = benefitApi.useGetBenefitCopySourceQuery(scope !== skipToken ? { ...scope, benefitId } : skipToken,
    { refetchOnMountOrArgChange: true })
  const [requested, setRequested] = useState(0)
  const [started, setStarted] = useState(0)
  useEffect(() => {
    const refresh = () => setRequested(value => value + 1)
    window.addEventListener("focus", refresh)
    return () => window.removeEventListener("focus", refresh)
  }, [])
  // Preserve this preview's existing focus behavior, but bound bursts to one in-flight
  // read plus a trailing read. A focus during GET cannot bless its older result.
  useEffect(() => {
    if (scope === skipToken || read.isUninitialized || read.isFetching || requested === started) return
    setStarted(requested)
    void read.refetch()
  }, [scope, read.isUninitialized, read.isFetching, read.refetch, requested, started])
  const current = scope !== skipToken && !read.isError && !read.isFetching && requested === started ? read.currentData : null
  const applicant = current?.id === benefitId ? current.personCollection.find(person => person.id === current.applicantId) : null
  return <div className="rounded-md border p-4 space-y-2">
    <h2 className="font-semibold">Current CMS application</h2>
    <p>{applicant ? `File ${current!.fileNumber}: ${formatPersonName(applicant)}` : "Current applicant is loading or unavailable. Open the file to verify; source matching choices are not its current identity."}</p>
    <Link className="underline" to={`/benefit-applications/${benefitId}?section=demographics`}>Open current file / correct applicant or household</Link>
    <p className="text-sm text-muted-foreground">Matching choices below are source-at-creation evidence. Corrections belong to the existing CMS file; they never create another file for this source.</p>
  </div>
}
